/** Timeline: ordered events with actor, timestamp (with zone) and detail — audit trails, claim histories. */
import { useIntl } from '@windrop/ui';
import type { ReactNode } from 'react';
import { ToneBadge, type Tone } from './StatusBadge';

export interface TimelineEvent {
  id: string;
  at: string;
  title: ReactNode;
  actor?: string;
  detail?: ReactNode;
  tone?: Tone;
}

export function Timeline({
  events,
  emptyText = 'Nothing recorded yet.',
}: {
  events: TimelineEvent[];
  emptyText?: string;
}) {
  const intl = useIntl();
  if (events.length === 0) return <p className="wd-muted">{emptyText}</p>;
  return (
    <ol className="wdc-timeline">
      {events.map((e) => (
        <li key={e.id} className="wdc-timeline__item" data-tone={e.tone ?? 'neutral'}>
          <span className="wdc-timeline__dot" aria-hidden="true" />
          <div className="wdc-timeline__body">
            <div className="wdc-timeline__head">
              <strong>{e.title}</strong>
              {e.tone && e.tone !== 'neutral' ? <ToneBadge tone={e.tone}>{e.tone}</ToneBadge> : null}
            </div>
            <p className="wd-muted">
              <time dateTime={e.at}>{intl.formatDateTime(new Date(e.at))}</time>
              {e.actor ? ` · ${e.actor}` : ''}
            </p>
            {e.detail ? <div className="wdc-timeline__detail">{e.detail}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
